// ============================================================================
// PROTECTED ROUTE COMPONENT
// Wraps routes that require authentication and/or a global permission
// ============================================================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  // Global Cedar action(s); any one of them is enough.
  requiredPermission?: string | string[];
  // Guests are denied every route by default, including those with no
  // requiredPermission, so each route they may reach has to opt in.
  guestAllowed?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  guestAllowed = false
}) => {
  const { isAuthenticated, loading, user, accessToken, canAny, isGuestOnly } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status OR waiting for token
  if (loading || (isAuthenticated && !accessToken)) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #052831',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    sessionStorage.setItem('auth_redirect', location.pathname);
    return <Navigate to="/login" replace />;
  }

  // Guests are default-deny: most routes carry no requiredPermission, so the check
  // below would otherwise wave them through. Send them to the one page they
  // can use rather than /unauthorized, since landing on a dead end straight
  // after signing in reads as a broken login.
  if (isGuestOnly && !guestAllowed) {
    return <Navigate to="/studio/book" replace />;
  }

  if (requiredPermission && user) {
    const actions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    if (!canAny(...actions)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
